import {
  RecordVoiceOverRounded as VoiceCloningIconSidebar,
  TheatersRounded as DubbingIconMobileSidebar,
  ShoppingCartRounded as PaymentIconSidebar,
  GraphicEqRounded as TTSIconMobileSidebar,
  CodeRounded as AIVoiceAPIIcon,
  MovieFilterRounded as MovieFilterRoundedIcon,
  FormatQuoteRounded,
  CardGiftcard as ReferralIcon,
  LocalLibraryRounded as VoiceLibraryIcon,
  HomeRounded as EducatorsHomeIcon,
} from '@mui/icons-material';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import { useSelector } from 'react-redux';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { VBEE_CONSOLE_URL } from '@src/configs';
import routes from '@src/constants/route';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useLocation from '@src/hooks/useLocation';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { PRODUCT_MODE } from '@src/constants/educators';
import { isVideoSource } from '@src/services/project';
import { useCheckDubbingRoute } from './useCheckDubbingRoute';

const useSidebarRoutes = () => {
  const isDubbingRoute = useCheckDubbingRoute();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const productMode = useSelector((state) => state.layout.productMode);
  // Narrow: this hook feeds both sidebars and the create-project popover, so
  // subscribing to the whole `project` slice re-rendered all three on every
  // keystroke in the editor — `product` is the only field it reads.
  const product = useSelector((state) => state.project.product);
  // Only replaced when a project loads, so as cheap as `product`.
  const source = useSelector((state) => state.project.source);
  const { ip } = useLocation();

  const speakerConfigEnabled = getFeatureValue(FEATURE_KEYS.SPEAKER_CONFIG, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const listAppsEnabled = getFeatureValue(FEATURE_KEYS.LIST_APPS, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const apiTtsSttLaunchEnabled = getFeatureValue(
    FEATURE_KEYS.LAUNCH_API_TTS_STT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    },
  );

  const educatorsModeEnabled = getFeatureValue(FEATURE_KEYS.EDUCATORS_MODE, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const getStudioProductName = () => 'newStudioName';

  const getDubbingProductName = () => 'newDubbingName';

  const sidebarRoutes = [
    {
      name: 'convertText',
      path: routes.TTS,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: getStudioProductName(),
      iconSidebar: TTSIconMobileSidebar,
    },
    {
      name: 'dubbing',
      path: routes.DUBBING,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: getDubbingProductName(),
      iconSidebar: DubbingIconMobileSidebar,
      featureKey: FEATURE_KEYS.DUBBING,
    },
    {
      name: 'project',
      path: routes.PROJECTS,
      isDesktopSidebar: true,
      labelSidebar: 'project',
      iconSidebar: MovieFilterRoundedIcon,
      beta: false,
      isMobileSidebar: true,
      isNewFeature: true,
    },
    {
      name: 'myVoice',
      path: routes.VOICE_CLONING_VOICES,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'voiceCloningLabel',
      iconSidebar: VoiceCloningIconSidebar,
      isNewFeature: !apiTtsSttLaunchEnabled,
    },
    {
      name: 'voiceCloning',
      path: `${routes.VOICE_CLONING_VOICES}?show-qrcode=true`,
      isMobileSidebar: false,
      isDesktopSidebar: false,
      labelSidebar: 'voiceCloningLabel',
      iconSidebar: VoiceCloningIconSidebar,
      beta: true,
    },
    {
      name: 'voiceLibrary',
      path: routes.VOICE_LIBRARY,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'voiceLibrary',
      iconSidebar: VoiceLibraryIcon,
    },
    {
      name: 'buyPackage',
      path: isDubbingRoute ? routes.PAYMENT_DUBBING : routes.PAYMENT,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'buyPackage',
      iconSidebar: PaymentIconSidebar,
      dataId: 'open-view-plan',
    },
    {
      name: 'referral',
      path: routes.REFERRAL,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: 'referral',
      iconSidebar: ReferralIcon,
      isNewFeature: false,
      isGradient: true,
    },
    {
      name: listAppsEnabled ? 'apiIntegration' : 'api',
      path: listAppsEnabled ? routes.LIST_APPS : VBEE_CONSOLE_URL,
      externalUrl: !listAppsEnabled,
      isMobileSidebar: true,
      isDesktopSidebar: true,
      labelSidebar: listAppsEnabled ? 'apiIntegration' : 'API',
      iconSidebar: AIVoiceAPIIcon,
      isNewFeature: apiTtsSttLaunchEnabled,
    },
    // Menu item for project detail page
    {
      name: 'project',
      path: routes.PROJECTS,
      isDesktopSidebar: false,
      labelSidebar: 'project',
      iconSidebar: MovieFilterRoundedIcon,
      isDetailProject: false,
      beta: true,
      subPaths: [routes.PROJECT_DETAIL],
    },
    {
      name: 'projectDictionary',
      isDesktopSidebar: true,
      labelSidebar: 'projectDictionary',
      isDetailProject: true,
    },
    {
      name: 'projectClientPause',
      isDesktopSidebar: true,
      labelSidebar: 'projectClientPause',
      iconSidebar: FormatQuoteRounded,
      isDetailProject: true,
    },
    {
      name: 'speakerSetting',
      isDesktopSidebar: true,
      labelSidebar: 'speakerSetting',
      iconSidebar: ManageAccountsRoundedIcon,
      isDetailProject: true,
    },
    {
      name: 'projectAudioType',
      isDesktopSidebar: true,
      labelSidebar: 'projectAudioType',
      iconSidebar: MovieFilterRoundedIcon,
      isDetailProject: true,
    },
    {
      name: 'projectCaption',
      isDesktopSidebar: true,
      labelSidebar: 'projectCaption',
      isDetailProject: true,
      featureKey: FEATURE_KEYS.VIDEO_DUBBING_CAPTION,
    },
  ];

  const educatorsHomeRoute = {
    name: 'educatorsHome',
    path: routes.EDUCATORS_HOME,
    isDesktopSidebar: true,
    labelSidebar: 'home',
    iconSidebar: EducatorsHomeIcon,
  };

  // Educators mode adds a home item on top, the rest of the menu is unchanged.
  // Desktop only, mobile has no educators mode.
  const desktopRoutes =
    educatorsModeEnabled && productMode === PRODUCT_MODE.EDUCATORS
      ? [educatorsHomeRoute, ...sidebarRoutes]
      : sidebarRoutes;

  const mobileSidebarRoutes = sidebarRoutes.filter(
    (route) => route.isMobileSidebar,
  );
  const desktopSidebarRoutes = desktopRoutes.filter(
    (route) => route.isDesktopSidebar && !route.isDetailProject,
  );
  const newProjectRoutes = sidebarRoutes.filter(
    (route) => route?.isCreateProject,
  );

  // Subtitles exist only in the video project editor.
  const isVideoDubbingProject =
    product === PROJECT_PRODUCT.DUBBING && isVideoSource(source);

  const detailProjectRoutes = sidebarRoutes.filter((route) => {
    if (route?.name === 'projectCaption' && !isVideoDubbingProject)
      return false;
    if (product === PROJECT_PRODUCT.DUBBING && speakerConfigEnabled) {
      return route?.isDetailProject && route?.name !== 'projectClientPause';
    }
    return route?.isDetailProject && route?.name !== 'speakerSetting';
  });

  return {
    mobileSidebarRoutes,
    desktopSidebarRoutes,
    newProjectRoutes,
    detailProjectRoutes,
  };
};

export default useSidebarRoutes;
